import React, { useEffect, useState, useCallback, useRef } from 'react';
import logo from '../photos/logo.png';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('home');

    const containerRef = useRef(null);
    const navRef = useRef(null);
    const barRef = useRef(null);
    const spacerRef = useRef(null); // NEW

    const toggleMenu = () => setIsMenuOpen(v => !v);

    const handleLogoClick = () => {
        setIsMenuOpen(false);
        if (window.location.pathname !== '/') {
            window.history.pushState({}, '', '/');
            window.dispatchEvent(new PopStateEvent('popstate'));
        }
        setActiveTab('home');
        window.location.hash = '#home';
    };

    // przełącz na '/' i ustaw hash po przełączeniu (np. dla #contact)
    const goHomeWithHash = (tab) => {
        const hash = `#${tab}`;
        setIsMenuOpen(false);
        setActiveTab(tab);

        if (window.location.pathname !== '/') {
            window.history.pushState({}, '', '/');
            window.dispatchEvent(new PopStateEvent('popstate'));
            setTimeout(() => { window.location.hash = hash; }, 0);
        } else {
            window.location.hash = hash;
        }
    };

    const handleHashTabClick = (tab) => {
        setActiveTab(tab);
        setIsMenuOpen(false);
    };

    // ESC zamyka menu
    const onKeyDown = useCallback((e) => { if (e.key === 'Escape') setIsMenuOpen(false); }, []);
    useEffect(() => {
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onKeyDown]);

    // === PRZELICZANIE WYSOKOŚCI HEADERA (bez „przeskoków”) ===
    const applyHeaderHeight = useCallback((hPx) => {
        const h = Math.max(0, Math.round(hPx || 0));
        document.documentElement.style.setProperty('--header-h', `${h}px`);
        if (containerRef.current) containerRef.current.style.setProperty('--header-h', `${h}px`);
        if (spacerRef.current) spacerRef.current.style.height = `${h}px`;
    }, []);

    const setHeaderHeightVar = useCallback(() => {
        const cont = containerRef.current;
        if (!cont) return;
        applyHeaderHeight(cont.offsetHeight);
    }, [applyHeaderHeight]);

    const setNavHeightVar = useCallback(() => {
        const cont = containerRef.current;
        const list = navRef.current?.querySelector('.nav-list');
        if (cont && list) cont.style.setProperty('--nav-h', `${list.scrollHeight}px`);
    }, []);

    useEffect(() => {
        setHeaderHeightVar();
        setNavHeightVar();
    }, [setHeaderHeightVar, setNavHeightVar, isMenuOpen, activeTab]);

    // NEW: ResizeObserver — łapie wszystkie zmiany wysokości (obrazy, fonty, otwarcie menu)
    useEffect(() => {
        const el = containerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(entries => {
            for (const entry of entries) {
                const box = entry.borderBoxSize?.[0] || entry.contentRect;
                const h = box?.blockSize || entry.target.offsetHeight;
                applyHeaderHeight(h);
            }
            setNavHeightVar();
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [applyHeaderHeight, setNavHeightVar]);

    // Reakcja na resize/scroll (zostawiamy jako uzupełnienie)
    useEffect(() => {
        const r = () => { setHeaderHeightVar(); setNavHeightVar(); };
        window.addEventListener('resize', r, { passive: true });
        window.addEventListener('scroll', r, { passive: true });
        // Po pełnym załadowaniu (iOS/Safari) jeszcze raz
        window.addEventListener('load', r);
        return () => {
            window.removeEventListener('resize', r);
            window.removeEventListener('scroll', r);
            window.removeEventListener('load', r);
        };
    }, [setHeaderHeightVar, setNavHeightVar]);

    // blokada scrolla tła tylko na mobile, gdy menu otwarte
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const original = document.body.style.overflow;
        if (mq.matches) document.body.style.overflow = isMenuOpen ? 'hidden' : (original || '');
        return () => { document.body.style.overflow = original; };
    }, [isMenuOpen]);

    // śledzenie pathname + hash → aktywna zakładka
    useEffect(() => {
        const setFromLocation = () => {
            const path = window.location.pathname;
            if (path === '/gallery') { setActiveTab('gallery'); return; }
            if (path === '/menu')   { setActiveTab('menu');    return; }
            const h = (window.location.hash || '#home').replace('#', '');
            if (['home','menu','contact'].includes(h)) setActiveTab(h);
            else setActiveTab('home');
        };
        setFromLocation();
        window.addEventListener('popstate', setFromLocation);
        window.addEventListener('hashchange', setFromLocation);
        return () => {
            window.removeEventListener('popstate', setFromLocation);
            window.removeEventListener('hashchange', setFromLocation);
        };
    }, []);

    // KONFIG
    const PHONE = '+48 52 30 777 55';
    const PHONE_TEL = PHONE.replace(/\s+/g, '');
    const FB_URL = 'https://www.facebook.com/p/Nier%C3%B3wno-Pod-Sufitem-100063583718251/?locale=pl_PL';

    return (
        <>
            <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        html, body { max-width: 100%; overflow-x: hidden; }
        .header-container, .header-main-bar, .header-nav { width: 100%; }

        :root {
          --primary-color: #C0583A;
          --text-color: #333;
          --light-bg: #f8f8f8;
          --border-color: #e3e3e3;
          --header-h: 88px;
          --nav-h: 0px;
        }

        [id] { scroll-margin-top: calc(var(--header-h) + 12px); }

        .header-container {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 9999;
          background: #fff;
          font-family: 'Inter', sans-serif;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .header-top-bar {
          background: var(--light-bg);
          text-align: center;
          padding: 6px clamp(12px, 3vw, 20px);
          border-bottom: 1px solid var(--border-color);
        }
        .header-top-bar a { color: var(--primary-color); font-weight: 600; text-decoration: none; }

        .header-main-bar {
          max-width: 1200px;
          margin: 0 auto;
          padding: clamp(8px, 1.4vw, 12px) clamp(12px, 3vw, 20px);
          display: grid;
          grid-template-columns: auto 1fr auto;
          align-items: center;
          gap: clamp(8px, 2vw, 16px);
          min-height: clamp(60px, 10vw, 88px);
          position: relative;
          background: #fff;
        }

        .header-logo a { display: inline-flex; align-items: center; }
        .header-logo img {
          height: clamp(64px, 8.2vw, 96px);
          width: auto;
          display: block; /* NEW: bez bazeline-shift */
          transition: transform .3s;
          cursor: pointer;
        }
        .header-logo img:hover { transform: translateY(-3px) scale(1.03); }

        .header-nav { justify-self: center; display: flex; justify-content: center; }
        .nav-list {
          display: flex; align-items: center;
          gap: clamp(16px, 3.2vw, 32px);
          margin: 0; padding: 0; list-style: none;
        }
        .nav-item a {
          position: relative;
          display: inline-block;
          padding: 8px 2px;
          line-height: 1.2;
          color: var(--text-color);
          text-decoration: none;
          font-weight: 500;
          transition: color .2s;
          white-space: nowrap;
        }
        .nav-item a:hover { color: var(--primary-color); }
        .nav-item a::after {
          content: '';
          position: absolute; left: 0; bottom: -3px;
          height: 2px; width: 0; background: var(--primary-color);
          transition: width .3s ease-out;
        }
        .nav-item a:hover::after, .nav-item a.active::after { width: 100%; }
        .nav-item a.active { color: var(--primary-color); }

        .right-cluster { display: inline-flex; align-items: center; gap: 12px; white-space: nowrap; }
        .contact-inline { display: inline-flex; align-items: center; gap: 10px; font-weight: 600; }
        .contact-inline a { color: var(--text-color); text-decoration: none; padding: 8px 6px; }
        .contact-inline a:hover { color: var(--primary-color); }
        .contact-inline .sep { opacity: .5; margin: 0 2px; }

        .menu-toggle {
          display: none;
          appearance: none;
          background: #fff;
          border: 1px solid var(--border-color);
          width: 44px; height: 44px;
          border-radius: 12px;
          padding: 0; margin: 0; line-height: 0; cursor: pointer; position: relative;
          box-shadow: 0 1px 2px rgba(0,0,0,.04), 0 4px 14px rgba(0,0,0,.06);
          transition: transform .15s ease, box-shadow .2s ease, border-color .2s ease, background .2s ease;
          touch-action: manipulation;
        }
        .menu-toggle:hover { transform: translateY(-1px); box-shadow: 0 6px 22px rgba(0,0,0,.08); border-color: #d6d6d6; }
        .menu-toggle:active { transform: translateY(0); }
        .menu-toggle:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }
        .menu-toggle .bar {
          display: block; width: 22px; height: 2px; background: var(--text-color);
          border-radius: 2px; position: absolute; left: 50%; transform: translateX(-50%);
          transition: transform .25s cubic-bezier(.2,.7,.1,1), opacity .2s ease, width .2s ease;
        }
        .menu-toggle .bar:nth-child(1) { top: 14px; transform-origin: left center; }
        .menu-toggle .bar:nth-child(2) { top: 21px; }
        .menu-toggle .bar:nth-child(3) { top: 28px; transform-origin: left center; }
        .menu-toggle.active .bar:nth-child(1) { transform: translateX(-50%) translateY(7px) rotate(45deg); width: 24px; }
        .menu-toggle.active .bar:nth-child(2) { opacity: 0; transform: translateX(calc(-50% - 6px)); }
        .menu-toggle.active .bar:nth-child(3) { transform: translateX(-50%) translateY(-7px) rotate(-45deg); width: 24px; }

        @media (max-width: 768px) {
          .menu-toggle { display: inline-block; justify-self: end; }
          .header-main-bar {
            grid-template-rows: auto auto;
            overflow-x: hidden;
          }
          .header-nav {
            grid-column: 1 / -1;
            grid-row: 2;
            position: static;
            width: 100%;
            background: transparent;
            border-top: 1px solid var(--border-color);
            box-shadow: inset 0 1px 0 rgba(0,0,0,0.03);
            overflow: hidden;
            max-height: 0;
            opacity: 0;
            transform: translateY(-6px);
            transition: max-height 320ms ease, opacity 220ms ease, transform 320ms ease;
          }
          .header-nav.open {
            max-height: min(var(--nav-h), 72vh);
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
            opacity: 1;
            transform: translateY(0);
          }

          .nav-list {
            flex-direction: column; align-items: flex-start;
            gap: 16px;
            padding: 14px clamp(18px, 5vw, 28px) 16px;
            width: 100%;
          }
          .nav-item a { padding: 10px 0; }
          .nav-item a::after { display: none; }

          .header-logo img { height: 78px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .header-logo img, .nav-item a::after, .header-nav, .menu-toggle, .menu-toggle .bar {
            transition: none !important;
          }
        }
      `}</style>

            <header className="header-container" ref={containerRef}>
                <div className="header-top-bar">
                    <a
                        href={FB_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Zobacz menu tygodnia na Facebooku"
                    >
                        ZOBACZ MENU TYGODNIA
                    </a>
                </div>

                <div className="header-main-bar" ref={barRef}>
                    {/* LOGO */}
                    <div className="header-logo">
                        <a href="/" onClick={(e)=>{ e.preventDefault(); handleLogoClick(); }} aria-label="Strona główna">
                            <img
                                src={logo}
                                alt="Logo firmy"
                                width="280" height="96"        // NEW: wymiary hint (nie skaluje na sztywno — CSS nadal rządzi)
                                decoding="async"                // NEW
                                onLoad={() => {                // NEW: po załadowaniu loga przeliczamy
                                    if (containerRef.current) applyHeaderHeight(containerRef.current.offsetHeight);
                                }}
                            />
                        </a>
                    </div>

                    {/* NAV */}
                    <nav
                        id="site-menu"
                        ref={navRef}
                        className={`header-nav ${isMenuOpen ? 'open' : ''}`}
                        role="navigation"
                        aria-label="Główne"
                    >
                        <ul className="nav-list">
                            {/* home */}
                            <li className="nav-item">
                                <a
                                    href="/"
                                    className={activeTab==='home' && window.location.pathname==='/' ? 'active' : ''}
                                    onClick={(e)=>{ e.preventDefault(); handleLogoClick(); }}
                                >
                                    Strona główna
                                </a>
                            </li>

                            {/* Menu → /menu (SPA) */}
                            <li className="nav-item">
                                <a
                                    href="/menu"
                                    className={window.location.pathname==='/menu' ? 'active' : ''}
                                    onClick={(e)=>{
                                        e.preventDefault();
                                        setIsMenuOpen(false);
                                        setActiveTab('menu');
                                        if (window.location.pathname !== '/menu') {
                                            window.history.pushState({}, '', '/menu');
                                            window.dispatchEvent(new PopStateEvent('popstate'));
                                        }
                                        window.scrollTo(0,0);
                                    }}
                                >
                                    Menu
                                </a>
                            </li>

                            {/* Galeria → /gallery */}
                            <li className="nav-item">
                                <a
                                    href="/gallery"
                                    className={window.location.pathname==='/gallery' ? 'active' : ''}
                                    onClick={(e)=>{
                                        e.preventDefault();
                                        setIsMenuOpen(false);
                                        setActiveTab('gallery');
                                        if (window.location.pathname !== '/gallery') {
                                            window.history.pushState({}, '', '/gallery');
                                            window.dispatchEvent(new PopStateEvent('popstate'));
                                        }
                                        window.scrollTo(0,0);
                                    }}
                                >
                                    Galeria
                                </a>
                            </li>

                            {/* Kontakt (hash na stronie głównej) */}
                            <li className="nav-item">
                                <a
                                    href="#contact"
                                    className={activeTab==='contact' ? 'active' : ''}
                                    onClick={(e)=>{
                                        e.preventDefault();
                                        goHomeWithHash('contact');
                                    }}
                                >
                                    Kontakt
                                </a>
                            </li>
                        </ul>
                    </nav>

                    {/* PRAWA STRONA: TEL | FB | (hamburger tylko mobile) */}
                    <div className="right-cluster">
                        <div className="contact-inline">
                            <a href={`tel:${PHONE_TEL}`}>{PHONE}</a>
                            <span className="sep">|</span>
                            <a
                                href={FB_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook – Nierówno Pod Sufitem"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                                </svg>
                            </a>
                        </div>

                        <button
                            className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
                            onClick={toggleMenu}
                            aria-label={isMenuOpen ? 'Zamknij menu' : 'Otwórz menu'}
                            aria-controls="site-menu"
                            aria-expanded={isMenuOpen ? 'true' : 'false'}
                            type="button"
                        >
                            <span className="bar" />
                            <span className="bar" />
                            <span className="bar" />
                        </button>
                    </div>
                </div>
            </header>

            {/* SPACER – pcha treść pod fixed header */}
            <div className="header-spacer" ref={spacerRef} style={{ height: 'var(--header-h)' }} />
        </>
    );
};

export default Header;
